import React from 'react';
import { Palette, Info } from 'lucide-react';
import { cn } from '../../lib/utils';
import { MapInteractivityModalShell } from '../map-interactivity/MapInteractivityModalShell';
import { MapIconPicker } from './MapIconPicker';
import { Input } from '../../internal/primitives/input';
import { Label } from '../../internal/primitives/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../internal/primitives/select';
import { Separator } from '../../internal/primitives/separator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../internal/primitives/accordion';
import type {
  MapIconPickerIconItem,
  MapIconPickerSetGroup,
} from '../../contracts/map-workspace';
import type { MapWorkbenchAdvancedRuleDefinition } from '../../contracts/map-workbench-advanced-rules';

type Representation = NonNullable<Exclude<MapWorkbenchAdvancedRuleDefinition['representation'], null>>;
type SymbolAuthoringMode = 'glyph' | 'sprite';
type RecordPatchValue = string | number | boolean | Array<string | number> | null | undefined;
type PropertyInputKind = 'color' | 'number' | 'text' | 'boolean' | 'select' | 'number-list' | 'string-list';

type PropertyDefinition = {
  prop: string;
  label: string;
  input: PropertyInputKind;
  advanced?: boolean;
  description?: string;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  fallback?: string;
  options?: Array<{ value: string; label: string }>;
}

type PropertySectionDefinition = {
  id: string;
  label: string;
  target: 'paint' | 'layout';
  properties: PropertyDefinition[];
}

type MapWorkbenchStyleEditorModalIconPickerConfig = {
  loading?: boolean;
  errorMessage?: string | null;
  unavailableMessage?: string | null;
  iconSets: MapIconPickerSetGroup[];
  setFilterOptions: Array<{ value: 'all' | string; label: string; disabled?: boolean }>;
  selectedSetId?: string | null;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  activeSetFilter?: 'all' | string;
  onSetFilterChange?: (value: 'all' | string) => void;
  onSelectIcon?: (selection: {
    iconId: string;
    token: string;
    setId: string;
    setLabel: string;
  }) => void;
}

export interface MapWorkbenchStyleEditorModalProps {
  open: boolean;
  rule: MapWorkbenchAdvancedRuleDefinition;
  onOpenChange: (open: boolean) => void;
  onUpdate: (patch: Partial<MapWorkbenchAdvancedRuleDefinition>) => void;
  representationOptions?: Array<{ value: string; label: string }>;
  defaultRepresentation?: string;
  allowRepresentationSelection?: boolean;
  iconPicker?: MapWorkbenchStyleEditorModalIconPickerConfig | null;
  forcedSymbolContentMode?: SymbolAuthoringMode | null;
}

function PropertyGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-x-6 gap-y-4">{children}</div>;
}

function renderPropertyList(
  properties: PropertyDefinition[],
  sectionTarget: 'paint' | 'layout',
  renderPropertyControl: (sectionTarget: 'paint' | 'layout', definition: PropertyDefinition) => React.ReactNode,
) {
  if (properties.length === 0) {
    return null;
  }

  return (
    <PropertyGrid>
      {properties.map(definition => (
        <StyleProperty
          key={definition.prop}
          label={definition.label}
          description={definition.description}
        >
          {renderPropertyControl(sectionTarget, definition)}
        </StyleProperty>
      ))}
    </PropertyGrid>
  );
}

function StyleProperty({ label, description, children }: { label: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5">
        <Label className="text-[11px] font-bold uppercase tracking-tight text-foreground/70">{label}</Label>
        {description && (
          <div title={description}>
            <Info className="h-3 w-3 text-muted-foreground/40" />
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

function isHexColor(value: string | null | undefined) {
  return Boolean(value) && /^#[0-9a-fA-F]{6}$/.test(value ?? '');
}

function ColorInput({
  value,
  fallback,
  onChange,
  onClear,
}: {
  value: string;
  fallback: string;
  onChange: (val: string) => void;
  onClear?: () => void;
}) {
  const swatchValue = isHexColor(value) ? value : fallback;

  return (
    <div className="flex gap-2">
      <div className="relative h-9 w-9 shrink-0 overflow-hidden rounded-lg border border-border shadow-sm">
        <Input
          type="color"
          className="absolute inset-0 h-[150%] w-[150%] -translate-x-[15%] -translate-y-[15%] cursor-pointer border-none p-0"
          value={swatchValue}
          onChange={e => onChange(e.target.value)}
        />
      </div>
      <Input
        className="h-9 flex-1 font-mono text-xs"
        value={value}
        placeholder={fallback}
        onChange={e => onChange(e.target.value)}
      />
      {onClear ? (
        <button
          type="button"
          className="rounded-lg border border-border px-3 text-[11px] font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          onClick={onClear}
        >
          Clear
        </button>
      ) : null}
    </div>
  );
}

const actionButtonClass =
  'inline-flex h-9 items-center justify-center rounded-lg px-4 text-sm font-semibold transition-all active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50';

const DEFAULT_REPRESENTATION_OPTIONS: Array<{ value: Representation; label: string }> = [
  { value: 'fill', label: 'Fill' },
  { value: 'line', label: 'Line' },
  { value: 'circle', label: 'Circle' },
  { value: 'symbol', label: 'Glyph / Sprite' },
  { value: 'fill-extrusion', label: 'Extrusion' },
];

const ANCHOR_OPTIONS = [
  { value: 'center', label: 'Center' },
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
  { value: 'top', label: 'Top' },
  { value: 'bottom', label: 'Bottom' },
  { value: 'top-left', label: 'Top Left' },
  { value: 'top-right', label: 'Top Right' },
  { value: 'bottom-left', label: 'Bottom Left' },
  { value: 'bottom-right', label: 'Bottom Right' },
];

const JUSTIFY_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];

const TRANSFORM_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'uppercase', label: 'Uppercase' },
  { value: 'lowercase', label: 'Lowercase' },
];

const fillSections: PropertySectionDefinition[] = [
  {
    id: 'fill-paint',
    label: 'Fill Paint',
    target: 'paint',
    properties: [
      { prop: 'fill-color', label: 'Fill Color', input: 'color', fallback: '#3b82f6' },
      { prop: 'fill-opacity', label: 'Opacity', input: 'number', min: 0, max: 1, step: 0.1 },
      { prop: 'fill-outline-color', label: 'Outline Color', input: 'color', fallback: '#0f172a' },
      { prop: 'fill-pattern', label: 'Pattern Token', input: 'text', placeholder: 'pattern-id' },
    ],
  },
  {
    id: 'fill-layout',
    label: 'Fill Layout',
    target: 'layout',
    properties: [
      { prop: 'fill-sort-key', label: 'Sort Key', input: 'number' },
    ],
  },
];

const lineSections: PropertySectionDefinition[] = [
  {
    id: 'line-paint',
    label: 'Line Paint',
    target: 'paint',
    properties: [
      { prop: 'line-color', label: 'Line Color', input: 'color', fallback: '#3b82f6' },
      { prop: 'line-width', label: 'Width', input: 'number', min: 0, step: 0.5 },
      { prop: 'line-opacity', label: 'Opacity', input: 'number', min: 0, max: 1, step: 0.1 },
      { prop: 'line-gap-width', label: 'Gap Width', input: 'number', min: 0, step: 0.5 },
      { prop: 'line-offset', label: 'Offset', input: 'number', step: 0.5 },
      { prop: 'line-blur', label: 'Blur', input: 'number', min: 0, step: 0.1 },
      { prop: 'line-pattern', label: 'Pattern Token', input: 'text', placeholder: 'pattern-id' },
    ],
  },
  {
    id: 'line-layout',
    label: 'Line Layout',
    target: 'layout',
    properties: [
      {
        prop: 'line-join',
        label: 'Join',
        input: 'select',
        options: [
          { value: 'miter', label: 'Miter' },
          { value: 'round', label: 'Round' },
          { value: 'bevel', label: 'Bevel' },
        ],
      },
      { prop: 'line-sort-key', label: 'Sort Key', input: 'number' },
    ],
  },
];

const circleSections: PropertySectionDefinition[] = [
  {
    id: 'circle-paint',
    label: 'Circle Paint',
    target: 'paint',
    properties: [
      { prop: 'circle-color', label: 'Circle Color', input: 'color', fallback: '#3b82f6' },
      { prop: 'circle-radius', label: 'Radius', input: 'number', min: 0, step: 0.5 },
      { prop: 'circle-opacity', label: 'Opacity', input: 'number', min: 0, max: 1, step: 0.1 },
      { prop: 'circle-blur', label: 'Blur', input: 'number', min: 0, step: 0.1 },
      { prop: 'circle-stroke-width', label: 'Stroke Width', input: 'number', min: 0, step: 0.5 },
      { prop: 'circle-stroke-color', label: 'Stroke Color', input: 'color', fallback: '#ffffff' },
      { prop: 'circle-stroke-opacity', label: 'Stroke Opacity', input: 'number', min: 0, max: 1, step: 0.1 },
    ],
  },
  {
    id: 'circle-layout',
    label: 'Circle Layout',
    target: 'layout',
    properties: [
      { prop: 'circle-sort-key', label: 'Sort Key', input: 'number' },
    ],
  },
];

const symbolSections: PropertySectionDefinition[] = [
  {
    id: 'symbol-layout',
    label: 'Symbol Placement',
    target: 'layout',
    properties: [
      { prop: 'symbol-sort-key', label: 'Sort Key', input: 'number', advanced: true },
    ],
  },
  {
    id: 'symbol-icon-layout',
    label: 'Sprite Layout',
    target: 'layout',
    properties: [
      { prop: 'icon-image', label: 'Icon Token', input: 'text', placeholder: 'icon-token', advanced: true },
      { prop: 'icon-size', label: 'Icon Size', input: 'number', min: 0, step: 0.1 },
      { prop: 'icon-rotate', label: 'Rotate', input: 'number', step: 1, advanced: true },
      { prop: 'icon-offset', label: 'Offset', input: 'number-list', placeholder: 'x, y', advanced: true },
      { prop: 'icon-anchor', label: 'Anchor', input: 'select', options: ANCHOR_OPTIONS, advanced: true },
    ],
  },
  {
    id: 'symbol-text-layout',
    label: 'Glyph Layout',
    target: 'layout',
    properties: [
      { prop: 'text-field', label: 'Text / Glyph', input: 'text', placeholder: '{name} or ▲' },
      { prop: 'text-size', label: 'Text Size', input: 'number', min: 0, step: 1 },
      { prop: 'text-font', label: 'Fonts', input: 'string-list', placeholder: 'Open Sans Regular, Arial Unicode MS Regular', advanced: true },
      { prop: 'text-max-width', label: 'Max Width', input: 'number', min: 0, step: 0.1, advanced: true },
      { prop: 'text-letter-spacing', label: 'Letter Spacing', input: 'number', step: 0.1, advanced: true },
      { prop: 'text-justify', label: 'Justify', input: 'select', options: JUSTIFY_OPTIONS, advanced: true },
      { prop: 'text-radial-offset', label: 'Radial Offset', input: 'number', step: 0.1, advanced: true },
      { prop: 'text-anchor', label: 'Anchor', input: 'select', options: ANCHOR_OPTIONS, advanced: true },
      { prop: 'text-rotate', label: 'Rotate', input: 'number', step: 1, advanced: true },
      { prop: 'text-transform', label: 'Transform', input: 'select', options: TRANSFORM_OPTIONS, advanced: true },
      { prop: 'text-offset', label: 'Offset', input: 'number-list', placeholder: 'x, y', advanced: true },
    ],
  },
  {
    id: 'symbol-icon-paint',
    label: 'Sprite Paint',
    target: 'paint',
    properties: [
      { prop: 'icon-color', label: 'Icon Color', input: 'color', fallback: '#111827', description: 'Applies only to tintable SDF icons.', advanced: true },
      { prop: 'icon-opacity', label: 'Icon Opacity', input: 'number', min: 0, max: 1, step: 0.1, advanced: true },
      { prop: 'icon-halo-color', label: 'Icon Halo Color', input: 'color', fallback: '#ffffff', advanced: true },
      { prop: 'icon-halo-width', label: 'Icon Halo Width', input: 'number', min: 0, step: 0.1, advanced: true },
      { prop: 'icon-halo-blur', label: 'Icon Halo Blur', input: 'number', min: 0, step: 0.1, advanced: true },
    ],
  },
  {
    id: 'symbol-text-paint',
    label: 'Glyph Paint',
    target: 'paint',
    properties: [
      { prop: 'text-color', label: 'Text Color', input: 'color', fallback: '#111827', advanced: true },
      { prop: 'text-opacity', label: 'Text Opacity', input: 'number', min: 0, max: 1, step: 0.1, advanced: true },
      { prop: 'text-halo-color', label: 'Text Halo Color', input: 'color', fallback: '#ffffff', advanced: true },
      { prop: 'text-halo-width', label: 'Text Halo Width', input: 'number', min: 0, step: 0.1, advanced: true },
      { prop: 'text-halo-blur', label: 'Text Halo Blur', input: 'number', min: 0, step: 0.1, advanced: true },
    ],
  },
];

const fillExtrusionSections: PropertySectionDefinition[] = [
  {
    id: 'fill-extrusion-paint',
    label: 'Extrusion Paint',
    target: 'paint',
    properties: [
      { prop: 'fill-extrusion-color', label: 'Color', input: 'color', fallback: '#3b82f6' },
      { prop: 'fill-extrusion-height', label: 'Height', input: 'number', min: 0, step: 1 },
      { prop: 'fill-extrusion-base', label: 'Base', input: 'number', min: 0, step: 1 },
      { prop: 'fill-extrusion-pattern', label: 'Pattern Token', input: 'text', placeholder: 'pattern-id' },
    ],
  },
];

const PROPERTY_SECTIONS_BY_REPRESENTATION: Record<Representation, PropertySectionDefinition[]> = {
  fill: fillSections,
  line: lineSections,
  circle: circleSections,
  symbol: symbolSections,
  'fill-extrusion': fillExtrusionSections,
}

const resolveSymbolAuthoringMode = (
  selectedIconToken: string | null,
  selectedGlyphValue: string,
): SymbolAuthoringMode => (selectedIconToken && !selectedGlyphValue.trim() ? 'sprite' : 'glyph');

const parseNumberValue = (raw: string) => {
  if (!raw.trim()) {
    return undefined;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}

const parseNumberListValue = (raw: string) => {
  if (!raw.trim()) {
    return undefined;
  }
  const parts = raw
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
  if (parts.length === 0) {
    return undefined;
  }
  const parsed = parts.map(part => Number(part));
  return parsed.every(value => Number.isFinite(value)) ? parsed : undefined;
}

const parseStringListValue = (raw: string) => {
  if (!raw.trim()) {
    return undefined;
  }
  const parts = raw
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts : undefined;
}

const formatListValue = (value: unknown) => {
  if (!Array.isArray(value)) {
    return '';
  }
  return value.map(item => String(item)).join(', ');
}

const findIconByToken = (
  iconSets: MapIconPickerSetGroup[],
  token: string | null | undefined,
  selectedSetId?: string | null,
) => {
  if (!token?.trim()) {
    return null;
  }

  if (selectedSetId) {
    const currentSet = iconSets.find(set => set.id === selectedSetId);
    const foundInCurrentSet = currentSet?.icons.find(icon => icon.token === token.trim());
    if (foundInCurrentSet) {
      return foundInCurrentSet;
    }
  }

  for (const set of iconSets) {
    const found = set.icons.find(icon => icon.token === token.trim());
    if (found) {
      return found;
    }
  }

  return null;
}

const setRecordProp = (
  current: Record<string, unknown>,
  prop: string,
  value: RecordPatchValue,
) => {
  const next = { ...current };
  if (
    value === undefined ||
    value === null ||
    (typeof value === 'string' && !value.trim())
  ) {
    delete next[prop];
  } else {
    next[prop] = value;
  }
  return Object.keys(next).length > 0 ? next : null;
}

export function MapWorkbenchStyleEditorModal({
  open,
  rule,
  onOpenChange,
  onUpdate,
  representationOptions = DEFAULT_REPRESENTATION_OPTIONS,
  defaultRepresentation = 'fill',
  allowRepresentationSelection = true,
  iconPicker = null,
  forcedSymbolContentMode = null,
}: MapWorkbenchStyleEditorModalProps) {
  const paint = React.useMemo(() => rule.paint ?? {}, [rule.paint]);
  const layout = React.useMemo(() => rule.layout ?? {}, [rule.layout]);
  const allowedRepresentations = new Set(representationOptions.map(option => option.value));
  const representation = (
    rule.representation && allowedRepresentations.has(rule.representation)
      ? rule.representation
      : defaultRepresentation
  ) as Representation;
  const [localIconSearch, setLocalIconSearch] = React.useState('');
  const [localSetFilter, setLocalSetFilter] = React.useState<'all' | string>(iconPicker?.activeSetFilter ?? 'all');

  React.useEffect(() => {
    if (iconPicker?.activeSetFilter) {
      setLocalSetFilter(iconPicker.activeSetFilter);
    }
  }, [iconPicker?.activeSetFilter]);

  const iconSearchValue = iconPicker?.searchValue ?? localIconSearch;
  const iconSetFilter = iconPicker?.activeSetFilter ?? localSetFilter;
  const setFilterOptions =
    iconPicker?.setFilterOptions && iconPicker.setFilterOptions.length > 0
      ? iconPicker.setFilterOptions
      : [{ value: 'all', label: 'All Icon Sets' }];

  const selectedIconToken = typeof layout['icon-image'] === 'string' ? layout['icon-image'] : null;
  const selectedGlyphValue = typeof layout['text-field'] === 'string' ? layout['text-field'] : '';
  const selectedIcon = React.useMemo<MapIconPickerIconItem | null>(
    () =>
      iconPicker
        ? findIconByToken(iconPicker.iconSets, selectedIconToken, iconPicker.selectedSetId)
        : null,
    [iconPicker, selectedIconToken],
  );
  const symbolMode = forcedSymbolContentMode ?? resolveSymbolAuthoringMode(selectedIconToken, selectedGlyphValue);
  const propertySections =
    representation === 'symbol'
      ? PROPERTY_SECTIONS_BY_REPRESENTATION.symbol.filter(section =>
          symbolMode === 'glyph'
            ? section.id !== 'symbol-icon-layout' && section.id !== 'symbol-icon-paint'
            : section.id !== 'symbol-text-layout' && section.id !== 'symbol-text-paint',
        )
      : PROPERTY_SECTIONS_BY_REPRESENTATION[representation];

  const updatePaintProp = React.useCallback(
    (prop: string, value: RecordPatchValue) => {
      onUpdate({ paint: setRecordProp(paint, prop, value) });
    },
    [onUpdate, paint],
  );

  const updateLayoutProp = React.useCallback(
    (prop: string, value: RecordPatchValue) => {
      onUpdate({ layout: setRecordProp(layout, prop, value) });
    },
    [layout, onUpdate],
  );

  const handleSelectIcon = React.useCallback(
    (iconId: string) => {
      if (!iconPicker) {
        return;
      }
      const icon = iconPicker.iconSets
        .flatMap(set => set.icons)
        .find(item => item.id === iconId);
      if (!icon) {
        return;
      }
      const nextLayout: Record<string, unknown> = { ...layout, 'icon-image': icon.token };
      onUpdate({ layout: nextLayout });
      iconPicker.onSelectIcon?.({
        iconId: icon.id,
        token: icon.token,
        setId: icon.setId,
        setLabel: icon.setLabel,
      });
    },
    [iconPicker, layout, onUpdate],
  );

  const renderPropertyControl = React.useCallback(
    (sectionTarget: 'paint' | 'layout', definition: PropertyDefinition) => {
      const currentValue =
        sectionTarget === 'paint' ? paint[definition.prop] : layout[definition.prop];
      const updateRecordProp = sectionTarget === 'paint' ? updatePaintProp : updateLayoutProp;

      switch (definition.input) {
        case 'color':
          return (
            <ColorInput
              value={typeof currentValue === 'string' ? currentValue : ''}
              fallback={definition.fallback ?? rule.color}
              onChange={value => updateRecordProp(definition.prop, value)}
              onClear={() => updateRecordProp(definition.prop, undefined)}
            />
          );
        case 'number':
          return (
            <Input
              type="number"
              min={definition.min}
              max={definition.max}
              step={definition.step}
              placeholder={definition.placeholder}
              value={typeof currentValue === 'number' ? String(currentValue) : ''}
              onChange={event => updateRecordProp(definition.prop, parseNumberValue(event.target.value))}
            />
          );
        case 'boolean':
          return (
            <Select
              value={
                typeof currentValue === 'boolean'
                  ? String(currentValue)
                  : '__default'
              }
              onValueChange={value =>
                updateRecordProp(
                  definition.prop,
                  value === '__default' ? undefined : value === 'true',
                )
              }
            >
              <SelectTrigger className="h-9 bg-muted/20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__default">Use layer default</SelectItem>
                <SelectItem value="true">True</SelectItem>
                <SelectItem value="false">False</SelectItem>
              </SelectContent>
            </Select>
          );
        case 'select':
          return (
            <Select
              value={typeof currentValue === 'string' ? currentValue : '__default'}
              onValueChange={value =>
                updateRecordProp(definition.prop, value === '__default' ? undefined : value)
              }
            >
              <SelectTrigger className="h-9 bg-muted/20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__default">Use layer default</SelectItem>
                {definition.options?.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        case 'number-list':
          return (
            <Input
              className="font-mono text-xs"
              placeholder={definition.placeholder}
              value={formatListValue(currentValue)}
              onChange={event => updateRecordProp(definition.prop, parseNumberListValue(event.target.value))}
            />
          );
        case 'string-list':
          return (
            <Input
              className="font-mono text-xs"
              placeholder={definition.placeholder}
              value={formatListValue(currentValue)}
              onChange={event => updateRecordProp(definition.prop, parseStringListValue(event.target.value))}
            />
          );
        case 'text':
        default:
          return (
            <Input
              className="font-mono text-xs"
              placeholder={definition.placeholder}
              value={typeof currentValue === 'string' ? currentValue : ''}
              onChange={event => updateRecordProp(definition.prop, event.target.value)}
            />
          );
      }
    },
    [layout, paint, rule.color, updateLayoutProp, updatePaintProp],
  );

  return (
    <MapInteractivityModalShell
      open={open}
      onOpenChange={onOpenChange}
      title={
        <div className="flex items-center gap-2">
          <Palette className="h-5 w-5 text-primary" />
          <span>Edit Style: {rule.label}</span>
        </div>
      }
      description="Configure visual properties for this rule. Changes apply to features matching the logic criteria."
      className="max-w-5xl"
    >
      <div className="space-y-8 py-2">
        {allowRepresentationSelection ? (
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Representation</Label>
              <Select
                value={representation}
                onValueChange={value => onUpdate({ representation: value as Representation })}
              >
                <SelectTrigger className="h-9 w-48 bg-muted/20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {representationOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Separator className="bg-border/40" />
          </section>
        ) : null}

        {representation === 'symbol' ? (
          <section className="space-y-4">
            {symbolMode === 'sprite' && iconPicker ? (
              <div className="space-y-2 rounded-xl border border-border/60 bg-muted/10 p-4">
                <MapIconPicker
                  presentation="inline"
                  value={selectedIconToken}
                  selectedIconId={selectedIcon?.id ?? null}
                  selectedSetId={iconPicker.selectedSetId}
                  searchValue={iconSearchValue}
                  activeSetFilter={iconSetFilter}
                  setFilterOptions={setFilterOptions}
                  iconSets={iconPicker.iconSets}
                  loading={iconPicker.loading}
                  errorMessage={iconPicker.errorMessage}
                  unavailableMessage={iconPicker.unavailableMessage}
                  allowClear
                  label="Sprite Icon"
                  triggerLabel="Choose Icon"
                  onSearchChange={value => (iconPicker.onSearchChange ?? setLocalIconSearch)(value)}
                  onSetFilterChange={value => (iconPicker.onSetFilterChange ?? setLocalSetFilter)(value)}
                  onSelect={handleSelectIcon}
                  onClear={() => updateLayoutProp('icon-image', undefined)}
                />
                {selectedIcon ? (
                  <div className="rounded-lg border border-border/50 bg-background/70 px-3 py-2 text-[11px] text-muted-foreground">
                    {selectedIcon.sdf
                      ? 'Selected icon is tintable. Icon color and halo properties will apply.'
                      : 'Selected icon uses baked colors. Icon color and halo properties will be ignored until you choose an SDF icon.'}
                  </div>
                ) : null}
              </div>
            ) : null}
            <Separator className="bg-border/40" />
          </section>
        ) : null}

        <section className="space-y-6">
          {propertySections.map(section => (
            <div key={section.id} className="space-y-4">
              {(() => {
                const basicProperties = section.properties.filter(definition => !definition.advanced);
                const advancedProperties = section.properties.filter(definition => definition.advanced);

                return (
                  <>
                    <div className="flex items-center justify-between">
                      <Label className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60">
                        {section.label}
                      </Label>
                      <span className="text-[10px] font-medium text-muted-foreground/70">
                        {section.target === 'paint' ? 'Paint overrides' : 'Layout overrides'}
                      </span>
                    </div>
                    {renderPropertyList(basicProperties, section.target, renderPropertyControl)}
                    {advancedProperties.length > 0 ? (
                      <Accordion type="single" collapsible className="rounded-xl border border-border/50 bg-muted/10">
                        <AccordionItem value={`${section.id}-advanced`} className="border-none">
                          <AccordionTrigger className="px-4 py-3 text-[11px] font-semibold text-muted-foreground hover:no-underline hover:text-foreground">
                            Advanced {section.label}
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-4 pt-1">
                            {renderPropertyList(advancedProperties, section.target, renderPropertyControl)}
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    ) : null}
                  </>
                );
              })()}
              <Separator className="bg-border/30" />
            </div>
          ))}
        </section>

        <div className="flex items-center justify-end gap-3 border-t border-border/40 pt-4">
          <button
            type="button"
            className={cn(actionButtonClass, 'border border-border bg-background text-foreground hover:bg-muted')}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className={cn(actionButtonClass, 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90')}
            onClick={() => onOpenChange(false)}
          >
            Done
          </button>
        </div>
      </div>
    </MapInteractivityModalShell>
  );
}
